// client/src/components/BidEstimation/VendorQuotes/ManualEntryModal.js
import React, { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, IconButton, Typography, Box, Grid,
  FormControl, InputLabel, Select, MenuItem
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import axios from 'axios'

const emptyRow = () => ({
  vendorSku: '',
  description: '',
  quantity: '',
  unitPrice: '',
  uom: ''
})

export default function ManualEntryModal ({ open, projectId, modules, onClose, onComplete }) {
  const [vendor, setVendor] = useState('')
  const [quoteNumber, setQuoteNumber] = useState('')
  const [selectedModuleId, setSelectedModuleId] = useState('')
  const [rows, setRows] = useState([emptyRow()])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const updateRow = (idx, field, value) => {
    const updated = [...rows]
    updated[idx] = { ...updated[idx], [field]: value }
    setRows(updated)
  }

  const addRow = () => setRows([...rows, emptyRow()])

  const removeRow = (idx) => {
    if (rows.length <= 1) return
    setRows(rows.filter((_, i) => i !== idx))
  }

  const handleSave = async () => {
    if (!vendor.trim()) {
      setError('Vendor name is required')
      return
    }

    setSaving(true)
    setError(null)
    try {
      // Create a VendorQuote with parseMethod "manual"
      const lineItems = rows
        .filter(r => r.description.trim())
        .map((r, idx) => ({
          lineNumber: idx + 1,
          vendorSku: r.vendorSku,
          description: r.description,
          quantity: parseFloat(r.quantity) || 0,
          unitPrice: parseFloat(r.unitPrice) || 0,
          uom: r.uom,
          extension: (parseFloat(r.quantity) || 0) * (parseFloat(r.unitPrice) || 0),
          mappingStatus: 'unmapped'
        }))

      // Use a direct POST to create the quote (no file upload)
      const { data } = await axios.post('/api/bid-estimation/vendor-quotes/upload', (() => {
        // For manual entry, we create via the update endpoint after creating a shell
        // Actually, we need a simpler approach: create the VendorQuote directly
        const formData = new FormData()
        formData.append('projectId', projectId)
        formData.append('manual', 'true')
        formData.append('vendor', vendor)
        formData.append('quoteNumber', quoteNumber)
        formData.append('lineItems', JSON.stringify(lineItems))
        if (selectedModuleId) formData.append('moduleId', selectedModuleId)
        return formData
      })(), {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      setVendor('')
      setQuoteNumber('')
      setRows([emptyRow()])
      onComplete(data)
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed')
    }
    setSaving(false)
  }

  const handleClose = () => {
    if (saving) return
    setVendor('')
    setQuoteNumber('')
    setRows([emptyRow()])
    setError(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle>Manual Quote Entry</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={6}>
            <TextField
              label='Vendor Name'
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              fullWidth
              variant='outlined'
              size='small'
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label='Quote Number'
              value={quoteNumber}
              onChange={(e) => setQuoteNumber(e.target.value)}
              fullWidth
              variant='outlined'
              size='small'
            />
          </Grid>
          {modules && modules.length > 0 && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Assign to Module (optional)</InputLabel>
                <Select value={selectedModuleId} onChange={e => setSelectedModuleId(e.target.value)} label="Assign to Module (optional)">
                  <MenuItem value="">None</MenuItem>
                  {modules.map(m => (
                    <MenuItem key={m._id} value={m._id}>{m.scope}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        {rows.map((row, idx) => (
          <Box key={idx} display='flex' gap={1} mb={1} alignItems='center' style={{ gap: 8 }}>
            <TextField
              label='SKU'
              value={row.vendorSku}
              onChange={(e) => updateRow(idx, 'vendorSku', e.target.value)}
              variant='outlined'
              size='small'
              style={{ width: 120 }}
            />
            <TextField
              label='Description'
              value={row.description}
              onChange={(e) => updateRow(idx, 'description', e.target.value)}
              variant='outlined'
              size='small'
              style={{ flex: 1 }}
            />
            <TextField
              label='Qty'
              value={row.quantity}
              onChange={(e) => updateRow(idx, 'quantity', e.target.value)}
              variant='outlined'
              size='small'
              type='number'
              style={{ width: 80 }}
            />
            <TextField
              label='Price'
              value={row.unitPrice}
              onChange={(e) => updateRow(idx, 'unitPrice', e.target.value)}
              variant='outlined'
              size='small'
              type='number'
              style={{ width: 100 }}
            />
            <TextField
              label='UOM'
              value={row.uom}
              onChange={(e) => updateRow(idx, 'uom', e.target.value)}
              variant='outlined'
              size='small'
              style={{ width: 80 }}
            />
            <IconButton size='small' onClick={() => removeRow(idx)} disabled={rows.length <= 1}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Box>
        ))}

        <Button startIcon={<AddIcon />} onClick={addRow} style={{ marginTop: 8 }}>
          Add Row
        </Button>

        {error && (
          <Typography color='error' variant='body2' style={{ marginTop: 16 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} color='primary' variant='contained' disabled={saving}>
          {saving ? 'Saving...' : 'Save Quote'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
